/**
 * Enterprise Authentication Utilities (LEGACY)
 *
 * DEPRECATION WARNING: These utilities are kept for backward compatibility only.
 * New code should use AuthService V2 directly.
 *
 * Most functions now delegate to AuthService V2 or provide minimal stubs.
 */

import { getApiBaseUrl } from '../config/runtimeUrls';

// ENTERPRISE V2: Minimal compatibility layer
// Uses canonical auth_token/refresh_token and falls back to legacy keys for reads.
const getAuthData = () => {
  try {
    const accessToken =
      localStorage.getItem('auth_token') ||
      localStorage.getItem('auth_v2_accessToken') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      '';
    if (!accessToken) return null;

    let user: any = null;
    let tenantId: any = null;
    let expiresAt: number | null = null;

    const userRaw =
      localStorage.getItem('auth_v2_user') ||
      localStorage.getItem('user') ||
      localStorage.getItem('userData') ||
      localStorage.getItem('authUser') ||
      localStorage.getItem('currentUser');
    if (userRaw) {
      user = JSON.parse(userRaw);
      tenantId = user?.tenantId || null;
    }

    if (!user) {
      const legacy = localStorage.getItem('authData');
      if (legacy) {
        const parsed = JSON.parse(legacy);
        user = parsed?.user || null;
        tenantId = parsed?.tenantId || parsed?.user?.tenantId || null;
        expiresAt = parsed?.expiresAt || null;
      }
    }

    if (!user) return null;

    return {
      token: accessToken,
      user,
      tenantId,
      expiresAt: expiresAt || Date.now() + 3600000,
    };
  } catch {
    return null;
  }
};

const clearAuthData = () => {
  const tokenKeys = [
    'auth_token',
    'refresh_token',
    'auth_v2_accessToken',
    'auth_v2_refreshToken',
    'accessToken',
    'refreshToken',
    'authToken',
    'token',
  ];
  tokenKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (_) {}
  });
};

// Enterprise type definitions
export interface DeviceInfo {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  ipAddress?: string;
  fingerprint: string;
  sessionId: string;
  timestamp: number;
  securityLevel: 'standard' | 'enhanced' | 'maximum';
  cookiesEnabled: boolean;
  doNotTrack: string | null;
  hardwareConcurrency: number;
  maxTouchPoints: number;
}

export interface EnterpriseUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'lawyer' | 'paralegal' | 'client' | 'super_admin';
  permissions: Permission[];
  department?: string;
  lastLogin: Date;
  securityClearance: number;
  mfaEnabled: boolean;
  sessionTimeout: number;
  organizationId?: string;
}

export interface Permission {
  action: string;
  resource: string;
  conditions?: Record<string, any>;
  level: 'read' | 'write' | 'admin';
}

export interface LoginResponse {
  success: boolean;
  token: string;
  refreshToken: string;
  user: EnterpriseUser;
  requiresMFA: boolean;
  mfaToken?: string;
  sessionId: string;
  expiresIn: number;
  permissions: Permission[];
  securityLevel: string;
}

// Token storage keys
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user',
  TENANT_ID: 'tenantId',
  AUTH_DATA: 'authData',
};

// Get auth token from centralized storage
export const getAuthToken = (): string | null => {
  const authData = getAuthData();
  return authData?.token || null;
};

// Set auth token in centralized storage
export const setAuthToken = (token: string): void => {
  // Always update legacy authData for compatibility (tests + older modules)
  try {
    const existingRaw = localStorage.getItem('authData');
    const existing = existingRaw ? JSON.parse(existingRaw) : {};
    const user = existing?.user || getAuthData()?.user || null;
    const tenantId = existing?.tenantId || user?.tenantId || getAuthData()?.tenantId || null;
    const next = {
      ...existing,
      token,
      user,
      tenantId,
      expiresAt: Date.now() + 3600000, // 1 hour
    };
    localStorage.setItem('authData', JSON.stringify(next));
  } catch (_) {}

  // Best-effort: also keep cache keys in sync if present
  try {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_v2_accessToken', token);
  } catch (_) {}
};

// Remove auth token
// Remove only cached access tokens; do not clear the entire session here
export const removeAuthToken = (): boolean => {
  let hadToken = false;
  const tokenKeys = [
    TOKEN_KEYS.ACCESS_TOKEN,
    TOKEN_KEYS.REFRESH_TOKEN,
    'auth_v2_accessToken',
    'auth_v2_refreshToken',
    'accessToken',
    'refreshToken',
    'authToken',
    'token',
  ];
  tokenKeys.forEach(key => {
    try {
      if (localStorage.getItem(key)) {
        hadToken = true;
        localStorage.removeItem(key);
      }
    } catch (_) {}
  });
  return hadToken;
};

/**
 * Check if user is authenticated
 * Validates token presence and basic format
 */
export const isAuthenticated = (): boolean => {
  try {
    const token = getAuthToken();
    if (!token) return false;

    // Check if token is expired (if expiry is stored)
    const authData = getAuthData();
    if (authData?.expiresAt && Date.now() > authData.expiresAt) {
      console.log('🔐 Auth utility: Token expired');
      // Passive check – do not clear full session here
      removeAuthToken();
      return false;
    }

    return true;
  } catch (error) {
    console.error('🔐 Auth utility: Error checking authentication:', error);
    return false;
  }
};

/**
 * ENTERPRISE USER INFORMATION MANAGEMENT
 * Gets comprehensive user information from token and localStorage
 */
export const getUserInfo = (): EnterpriseUser | null => {
  try {
    const authData = getAuthData();
    if (!authData) return null;

    const user = authData.user;
    if (!user) return null;

    return {
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role as any, // Type assertion for backward compatibility
      permissions: [], // Default empty array
      department: '', // Default empty string
      lastLogin: new Date(), // Default current date
      securityClearance: 1, // Default security clearance
      mfaEnabled: false, // Default MFA disabled
      sessionTimeout: 3600, // Default session timeout
      organizationId: user.tenantId, // Use tenantId as organizationId
    };
  } catch (error) {
    console.error('🔐 Auth utility: Error getting user info:', error);
    return null;
  }
};

/**
 * ENTERPRISE PERMISSION MANAGEMENT SYSTEM
 * Comprehensive permission checking with role-based access control
 */
export const hasPermission = (
  requiredPermission: string,
  resource?: string,
  level: 'read' | 'write' | 'admin' = 'read'
): boolean => {
  try {
    const user = getUserInfo();
    if (!user) return false;

    // Super admin and admin users have all permissions
    if (user.role === 'super_admin' || user.role === 'admin') return true;

    // Check specific permissions
    return user.permissions.some(permission => {
      const actionMatch = permission.action === requiredPermission || permission.action === '*';
      const resourceMatch =
        !resource || permission.resource === resource || permission.resource === '*';
      const levelMatch = permission.level === level || permission.level === 'admin';

      return actionMatch && resourceMatch && levelMatch;
    });
  } catch (error) {
    console.error('🔐 Auth utility: Permission check failed:', error);
    return false;
  }
};

/**
 * ENTERPRISE ROLE-BASED ACCESS CONTROL
 * Check if user has specific role(s)
 */
export const hasRole = (requiredRole: string | string[]): boolean => {
  const user = getUserInfo();
  if (!user) return false;

  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  return roles.includes(user.role);
};

/**
 * ENTERPRISE SECURITY LEVEL VALIDATION
 * Check if user has required security clearance
 */
export const hasSecurityClearance = (requiredLevel: number): boolean => {
  const user = getUserInfo();
  if (!user) return false;

  return user.securityClearance >= requiredLevel;
};

/**
 * Get token expiry time
 */
export const getTokenExpiry = (): number | null => {
  try {
    const authData = getAuthData();
    return authData?.expiresAt || null;
  } catch (error) {
    console.error('🔐 Auth utility: Error getting token expiry:', error);
    return null;
  }
};

/**
 * Set token expiry time
 */
export const setTokenExpiry = (expiryTime: number): void => {
  try {
    const authData = getAuthData();
    if (authData) {
      authData.expiresAt = expiryTime;
      localStorage.setItem('authData', JSON.stringify(authData));
    }
  } catch (error) {
    console.error('🔐 Auth utility: Error setting token expiry:', error);
  }
};

/**
 * Get session ID
 */
export const getSessionId = (): string | null => {
  try {
    // Session ID not stored in centralized auth data, return null
    return null;
  } catch (error) {
    console.error('🔐 Auth utility: Error getting session ID:', error);
    return null;
  }
};

/**
 * Set session ID
 */
export const setSessionId = (sessionId: string): void => {
  try {
    // Session ID not stored in centralized auth data, do nothing
    console.log('Session ID setting not supported in centralized auth system');
  } catch (error) {
    console.error('🔐 Auth utility: Error setting session ID:', error);
  }
};

/**
 * Get last activity timestamp
 */
export const getLastActivity = (): number | null => {
  try {
    // Last activity not stored in centralized auth data, return current time
    return Date.now();
  } catch (error) {
    console.error('🔐 Auth utility: Error getting last activity:', error);
    return null;
  }
};

/**
 * Update last activity timestamp
 */
export const updateLastActivity = (): void => {
  try {
    // Last activity not stored in centralized auth data, do nothing
    console.log('Last activity update not supported in centralized auth system');
  } catch (error) {
    console.error('🔐 Auth utility: Error updating last activity:', error);
  }
};

/**
 * Clear all authentication data
 */
export const clearAllAuthData = (): void => {
  try {
    // Clear token keys only; do not clear app or user state
    clearAuthData();

    console.log('🔐 Auth utility: All authentication data cleared');
  } catch (error) {
    console.error('🔐 Auth utility: Error clearing all auth data:', error);
    throw error;
  }
};

/**
 * Validate token format (basic validation)
 */
export const validateTokenFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') return false;

  // Basic JWT format validation (3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length !== 3) return false;

  // Check if parts are not empty
  if (parts.some(part => !part)) return false;

  return true;
};

/**
 * Parse JWT token payload (without verification)
 */
export const parseTokenPayload = (token: string): any => {
  try {
    if (!validateTokenFormat(token)) {
      throw new Error('Invalid token format');
    }

    const payload = token.split('.')[1];
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('🔐 Auth utility: Error parsing token payload:', error);
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseTokenPayload(token);
    if (!payload || !payload.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('🔐 Auth utility: Error checking token expiration:', error);
    return true;
  }
};

/**
 * Get token claims
 */
export const getTokenClaims = (token: string): any => {
  try {
    return parseTokenPayload(token);
  } catch (error) {
    console.error('🔐 Auth utility: Error getting token claims:', error);
    return null;
  }
};

/**
 * Refresh token activity
 */
export const refreshTokenActivity = (): void => {
  try {
    updateLastActivity();

    // Check if token needs refresh based on expiry
    const token = getAuthToken();
    if (token && isTokenExpired(token)) {
      console.log('🔐 Auth utility: Token expired');
      // Do not clear proactively here; axios 401 interceptor will handle logout
    }
  } catch (error) {
    console.error('🔐 Auth utility: Error refreshing token activity:', error);
  }
};

// Token refresh with centralized auth system
let refreshInProgress: Promise<string | null> | null = null;

export const refreshToken = async (): Promise<string | null> => {
  if (refreshInProgress) return refreshInProgress;

  try {
    refreshInProgress = new Promise(async resolve => {
      try {
        const authData = getAuthData();
        if (!authData) {
          // No session available; treat as graceful failure
          resolve(null);
          return;
        }

        // Use HTTP-only refresh cookie flow; no body required
        const response = await fetch(
          `${getApiBaseUrl()}/auth/refresh-token`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error('Token refresh failed');
        }

        const payload = await response.json();
        // Support both { success, data: {...} } and flat shape
        const data = payload && payload.success && payload.data ? payload.data : payload;

        const newAccessToken: string | undefined = data?.accessToken || data?.token;
        const newUser = data?.user || authData.user;
        const newTenantId: string | undefined =
          data?.tenantId || data?.user?.tenantId || authData.tenantId;

        if (!newAccessToken) {
          throw new Error('Token refresh response missing accessToken');
        }

        // Update stored auth data
        const updatedAuthData = {
          ...authData,
          token: newAccessToken,
          user: newUser,
          tenantId: newTenantId,
          expiresAt: Date.now() + (data?.expiresIn ? data.expiresIn * 1000 : 3600000),
        };

        localStorage.setItem('authData', JSON.stringify(updatedAuthData));
        // Keep API client in sync by updating accessToken cache
        try {
          localStorage.setItem('auth_token', newAccessToken);
          localStorage.setItem('auth_v2_accessToken', newAccessToken);
        } catch (_) {
          // no-op
        }
        if (newTenantId) {
          try {
            localStorage.setItem('tenantId', newTenantId);
          } catch (_) {}
        }

        console.log('✅ Token refresh successful');
        resolve(newAccessToken);
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Do not clear proactively; report null and let interceptor handle sign-out on real 401
        resolve(null);
      } finally {
        refreshInProgress = null;
      }
    });

    return refreshInProgress;
  } catch (error) {
    return null;
  }
};

export const logout = (): void => {
  clearAuthData();
  console.info('✅ User logged out');
};

// Export token keys for external use
export { TOKEN_KEYS };
